// File: src/pages/ConnectSuccessPage.jsx

import React, { useEffect, useContext, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Spinner,
  Banner,
  Button,
  Box,
} from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import { markShopSetupComplete } from '../hooks/useShopifyEmbeddedRedirect'; // ADDED

const API = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://api.baobrain.com'; // Ensure no trailing slash

export default function ConnectSuccessPage() {
  const { token, setToken } = useContext(AuthContext); // Your JWT is managed by AuthContext
  const navigate = useNavigate();
  const location = useLocation();

  // Extract Shopify-related parameters from URL query string
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shopDomain = queryParams.get('shop');
  const siteId = queryParams.get('site_id'); // siteId is initially a string from URL
  const siteToken = queryParams.get('site_token');
  const shopifyAccessToken = queryParams.get('access_token'); // The Shopify access token passed from backend

  // Moved useState calls to the top level, unconditionally
  const [isLoading, setIsLoading] = useState(true);
  const [claimError, setClaimError] = useState(null);
  const [ga4DetectError, setGa4DetectError] = useState(null);
  const [claimedByOther, setClaimedByOther] = useState(false); // NEW
  const [siteLimit, setSiteLimit] = useState(false);           // NEW


  console.log(
    `[ConnectSuccessPage] rendered — URL: ${location.pathname}${location.search}`
  );
  console.log('[ConnectSuccessPage] Rendered.');
  console.log('[ConnectSuccessPage] URL Params:', { shopDomain, siteId, siteToken, shopifyAccessToken: shopifyAccessToken ? 'present' : 'absent' });
  console.log('[ConnectSuccessPage] Auth Token (AuthContext):', token ? 'present' : 'absent');


  // Moved useEffect to be after all useState calls and before any conditional returns
  useEffect(() => {
    // Nested function for GA4 detection to keep it stable within this useEffect
    const detectGA4Snippet = async () => {
      try {
        const res = await fetch(`${API}/integrations/detect-or-extract`, {
          method: 'POST',
          headers: {
            'Content-Type': "application/json",
            Authorization: `Bearer ${token}`,
          },
          // MODIFIED: Only send the shop domain in the body
          body: JSON.stringify({ shop: shopDomain })
        });

        // Handle 401 Unauthorized for GA4 detection API call
        if (res.status === 401) {
          console.warn('[ConnectSuccessPage] 401 Unauthorized during GA4 detection. Clearing token and redirecting to login.');
          setToken(null); // Clear the bad token
          navigate(
            `/login` +
              `?shop=${encodeURIComponent(shopDomain || '')}` +
              `&site_id=${siteId || ''}` +
              `&site_token=${siteToken || ''}` +
              `&access_token=${shopifyAccessToken || ''}` +
              `&return_to=${encodeURIComponent(location.pathname + location.search)}`
          );
          setIsLoading(false);
          return;
        }

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          const errorMessage = errorData.detail || `Failed to detect GA4 snippet: Status ${res.status}`;
          console.error('[ConnectSuccessPage] GA4 detection failed:', errorMessage);
          setGa4DetectError(errorMessage);
          setIsLoading(false);
          return;
        }

        const { detected } = await res.json();
        console.log('[ConnectSuccessPage] GA4 detection result:', detected);

        setIsLoading(false);

        // After successful GA4 detection:
        if (detected) {
          console.log('[ConnectSuccessPage] GA4 detected. Marking shop setup complete and redirecting to dashboard.');
          const shop = shopDomain;
          const host = queryParams.get('host');

          if (shop) {
            markShopSetupComplete(shop);
            
            // Optional: Redirect to dashboard after a delay
            setTimeout(() => {
              if (host) {
                navigate(`/dashboard?shop=${shop}&host=${host}`);
              } else {
                navigate('/dashboard');
              }
            }, 2000); // 2 second delay
            return;
          }
        }
        
        // Navigate to Integrations page using React Router if not detected or shop is missing
        console.log('[ConnectSuccessPage] GA4 not detected. Proceeding to integrations page for setup.');
        navigate('/integrations');
        return;

      } catch (e) {
        console.error('[ConnectSuccessPage] GA4 detection error:', e);
        setGa4DetectError(e.message || 'We couldn’t check your theme for a GA4 snippet.');
        setIsLoading(false);
      }
    };


    async function handleShopifyConnectFlow() {
      setIsLoading(true);
      setClaimError(null);
      setGa4DetectError(null);

      // Scenario 1: User is NOT authenticated → bounce to Login
      if (!token) {
        console.log('[ConnectSuccessPage] No JWT; redirecting to Login before integrations.');
        navigate(
          `/login` +
            `?shop=${encodeURIComponent(shopDomain || '')}` +
            `&site_id=${siteId || ''}` +
            `&site_token=${siteToken || ''}` +
            `&access_token=${shopifyAccessToken || ''}` +
            `&return_to=${encodeURIComponent(location.pathname + location.search)}`
        );
        setIsLoading(false);
        return;
      }

      // Scenario 2: User IS authenticated, but site_id/site_token are missing
      if (!siteId || !siteToken) {
        console.warn('[ConnectSuccessPage] Authenticated but missing site_id/site_token. Navigating to dashboard.');
        setGa4DetectError('Missing site details for connection. Please try again from your sites list.');
        setIsLoading(false);
        navigate('/dashboard');
        return;
      }

      // Scenario 3: User IS authenticated AND we have site details. Attempt to claim.
      console.log(`[ConnectSuccessPage] User authenticated. Attempting to claim site_id=${siteId}`);
      try {
        const claimRes = await fetch(`${API}/api/sites/claim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          // Keep parseInt(siteId) here for the claim endpoint, as it might expect an integer
          // body: JSON.stringify({ site_id: parseInt(siteId), site_token: siteToken, shopify_access_token: shopifyAccessToken }),
          // If your backend's /api/sites/claim also expects site_id as a string, change this to `site_id: siteId`
          // For consistency, if you're doing the "hack," consider if this endpoint also needs the string.
          // However, typically IDs are integers, so it's safer to leave this as parseInt unless you confirm
          // your /api/sites/claim endpoint specifically expects a string for site_id.
          body: JSON.stringify({ site_id: parseInt(siteId), site_token: siteToken, shopify_access_token: shopifyAccessToken }),
        });

        // Handle 401 Unauthorized for claim API call
        if (claimRes.status === 401) {
          console.warn('[ConnectSuccessPage] 401 Unauthorized during site claim. Clearing token and redirecting to login.');
          setToken(null);
          navigate(
            `/login` +
                  `?shop=${encodeURIComponent(shopDomain || '')}` +
                  `&site_id=${siteId || ''}` +
                  `&site_token=${siteToken || ''}` +
                  `&access_token=${shopifyAccessToken || ''}` +
                  `&return_to=${encodeURIComponent(location.pathname + location.search)}`
          );
          setIsLoading(false);
          return;
        }

        if (!claimRes.ok) {
          const errorData = await claimRes.json().catch(() => ({}));
          const msg = (errorData.detail || `Failed to link Shopify store: Status ${claimRes.status}`).toLowerCase();
          console.error('[ConnectSuccessPage] Site claim failed:', msg);
        
          if (claimRes.status === 409) {
            // Case A: Claimed by another user
            if (msg.includes('claimed by another user')) {
              setClaimedByOther(true);
              setClaimError('This store has already been claimed by another user. If you suspect this is an error, contact support.');
            }
            // Case B: User already has a site (one site per account)
            else if (
              msg.includes('only connect one site') ||
              msg.includes('one site per account') ||
              msg.includes('you may only connect one site')
            ) {
              setSiteLimit(true);
              setClaimError('You may only connect one site per account. If you want to replace the site connected to your account you can do this in Settings.');
            } else {
              setClaimError(errorData.detail || 'Failed to link Shopify store.');
            }
          } else {
            setClaimError(errorData.detail || 'Failed to link Shopify store.');
          }
        
          setIsLoading(false);
          return;
        }
        

        console.log('[ConnectSuccessPage] Site claimed successfully. Proceeding with GA4 detection.');

        // *** NEW: Persist site_id and site_token in sessionStorage ***
        sessionStorage.setItem('site_id', siteId);
        sessionStorage.setItem('site_token', siteToken);
        sessionStorage.setItem('shop_domain', shopDomain); // ADDED: Persist shop_domain

        await detectGA4Snippet(); // Call detectGA4Snippet only after successful claim

      } catch (err) {
        console.error('[ConnectSuccessPage] Error during site claim API call:', err);
        setClaimError(err.message || 'Network error during site linking.');
        setIsLoading(false);
      }
    }

    handleShopifyConnectFlow();
  }, [token, shopDomain, siteId, siteToken, shopifyAccessToken, location.pathname, location.search, setToken, navigate, queryParams]); // Added queryParams to dependency array for host access


  // --- Render Logic ---

  // Guard against missing shopDomain - This can now be safely placed after hook calls
  if (!shopDomain) {
    return (
      <Page title="Invalid Link">
        <Card sectioned>
          <Banner status="critical" title="Missing shop parameter">
            We couldn’t find which shop to connect. Please start the flow again.
          </Banner>
        </Card>
      </Page>
    );
  }
// Claimed by another user → Contact Support or Dashboard
if (claimedByOther) {
  return (
    <Page title="Store Already Claimed">
      <Card sectioned>
        <Banner status="critical" title="This store has already been claimed by another user.">
          If you believe this is an error, please contact support.
        </Banner>
        <Box paddingBlockStart="4" textAlign="center">
          <Button primary onClick={() => navigate('/ContactUs')}>Contact Support</Button>
          <Button plain onClick={() => navigate('/dashboard')} style={{ marginLeft: '1rem' }}>
            Go to Dashboard
          </Button>
        </Box>
      </Card>
    </Page>
  );
}

// User already has a site → Settings or Dashboard
if (siteLimit) {
  return (
    <Page title="Site Limit Reached">
      <Card sectioned>
        <Banner status="critical" title="You may only connect one site per account.">
          If you want to replace the site connected to your account you can do this in Settings.
        </Banner>
        <Box paddingBlockStart="4" textAlign="center">
          <Button primary onClick={() => navigate('/settings')}>Go to Settings</Button>
          <Button plain onClick={() => navigate('/dashboard')} style={{ marginLeft: '1rem' }}>
            Go to Dashboard
          </Button>
        </Box>
      </Card>
    </Page>
  );
}


  // Display claim error
  if (claimError) {
    return (
      <Page title="Connection Error">
        <Card sectioned>
          <Banner status="critical" title={claimError}>
          </Banner>
          <Box paddingBlockStart="4" textAlign="center">
            <Button
              primary
              onClick={() => {
                navigate(`/connect-shopify?shop=${encodeURIComponent(shopDomain || '')}`);
              }}
            >
              Try Again
            </Button>
            <Button
              plain
              onClick={() => navigate('/dashboard')}
              style={{ marginLeft: '1rem' }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </Card>
      </Page>
    );
  }

  // Display GA4 detection error
  if (ga4DetectError) {
    return (
      <Page title="Setup Error">
        <Card sectioned>
          <Banner status="critical" title="Setup Incomplete">
            {ga4DetectError}
          </Banner>
          <Box paddingBlockStart="4" textAlign="center">
            <Button
              primary
              onClick={() => {
                navigate('/integrations');
              }}
            >
              Go to Integrations
            </Button>
            <Button
              plain
              onClick={() => {
                navigate('/dashboard');
              }}
              style={{ marginLeft: '1rem' }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </Card>
      </Page>
    );
  }

  // Initial loading and processing state - only show if no errors and still loading
  if (isLoading) {
    return (
      <Page title="Finalizing Setup">
        <Card sectioned>
          <Banner status="info" title="Almost there…">
            Connecting your Shopify store. Please wait.
          </Banner>
          <Box paddingBlockStart="4" textAlign="center">
            <Spinner size="large" accessibilityLabel="Connecting your Shopify store" />
          </Box>
        </Card>
      </Page>
    );
  }

  // Fallback for cases where loading finishes but no redirect/error occurs.
  return null;
}